import React, { useState } from "react";
import { Link, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { authAPI } from "@/lib/api";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import NotificationSettings from "@/components/shared/NotificationSettings";
import DashboardOverview from "@/components/user/DashboardOverview";
import MyBookings from "@/components/user/MyBookings";
import MyDeliveries from "@/components/user/MyDeliveries";
import { 
  Package, 
  LogOut,
  LayoutDashboard,
  BookOpen,
  Loader2,
  RefreshCw,
  Menu,
  X,
  Bell,
  Search,
  Plane,
  Zap
} from "lucide-react";

// Main User Dashboard Component
export default function UserDashboard() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [switchingRole, setSwitchingRole] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleSwitchToPartner = async () => {
    setSwitchingRole(true);
    try {
      const response = await authAPI.switchRole();
      toast.success("Switched to Partner account!");
      login(response.data);
      // Use window.location for a hard redirect to ensure state is fresh
      window.location.href = "/partner";
    } catch {
      toast.error("Failed to switch role. Please try again.");
      setSwitchingRole(false);
    }
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/bookings", label: "My Bookings", icon: BookOpen },
    { path: "/dashboard/deliveries", label: "My Deliveries", icon: Package },
    { path: "/dashboard/notifications", label: "Notifications", icon: Bell },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          {/* Mobile menu button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
            
          </Link>
          
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/browse">
              <Button variant="ghost" className="rounded-none hidden md:flex" data-testid="header-browse-btn">
                <Search className="w-4 h-4 mr-2" />
                Browse
              </Button>
            </Link>
            <Link to="/private-jets">
              <Button variant="ghost" className="rounded-none hidden md:flex" data-testid="header-jets-btn">
                <Plane className="w-4 h-4 mr-2" />
                Jets
              </Button>
            </Link>
            <Link to="/local-delivery">
              <Button variant="ghost" className="rounded-none hidden md:flex" data-testid="header-local-delivery-btn">
                <Zap className="w-4 h-4 mr-2" />
                Local Delivery
              </Button>
            </Link>
            <NotificationBell />
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed left-0 top-16 bottom-0 w-72 bg-background border-r border-border p-4 overflow-y-auto">
            {/* User Info */}
            <div className="pb-4 mb-4 border-b border-border">
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            
            {/* Quick Actions */}
            <div className="mb-6">
              <h3 className="font-bold text-sm mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <Link to="/local-delivery" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full rounded-none justify-start bg-accent text-accent-foreground">
                    <Zap className="w-4 h-4 mr-2" />
                    Book Local Delivery
                  </Button>
                </Link>
                <Link to="/browse" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full rounded-none justify-start">
                    <Search className="w-4 h-4 mr-2" />
                    Find Cargo Space
                  </Button>
                </Link>
                <Link to="/private-jets" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full rounded-none justify-start">
                    <Plane className="w-4 h-4 mr-2" />
                    Book Private Jet
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="space-y-1 mb-6">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            
            {/* Switch to Partner */}
            <div className="pt-4 border-t border-border">
              <Button
                variant="outline"
                className="w-full rounded-none"
                onClick={handleSwitchToPartner}
                disabled={switchingRole}
              >
                {switchingRole ? "Switching..." : "Switch to Partner"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:block w-64 border-r border-border min-h-[calc(100vh-64px)] p-4">
          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-secondary'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          
          <div className="mt-8 p-4 border border-border bg-secondary/30">
            <h3 className="font-bold text-sm mb-2">Quick Actions</h3>
            <div className="space-y-2">
              <Link to="/local-delivery" className="block">
                <Button variant="default" size="sm" className="w-full rounded-none justify-start bg-accent text-accent-foreground">
                  <Zap className="w-4 h-4 mr-2" />
                  Book Local Delivery
                </Button>
              </Link>
              <Link to="/browse" className="block">
                <Button variant="outline" size="sm" className="w-full rounded-none justify-start">
                  <Search className="w-4 h-4 mr-2" />
                  Find Cargo Space
                </Button>
              </Link>
              <Link to="/private-jets" className="block">
                <Button variant="outline" size="sm" className="w-full rounded-none justify-start">
                  <Plane className="w-4 h-4 mr-2" />
                  Book Private Jet
                </Button>
              </Link>
            </div>
          </div>

          {/* Switch to Partner */}
          <div className="mt-4 p-4 border border-primary/30 bg-primary/5">
            <h3 className="font-bold text-sm mb-2">Want to list your space?</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Switch to Partner mode to list your cargo space, jets, or local delivery routes.
            </p>
            <Button 
              onClick={handleSwitchToPartner}
              disabled={switchingRole}
              className="w-full rounded-none bg-primary text-primary-foreground"
              data-testid="switch-to-partner-btn"
            >
              {switchingRole ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Switch to Partner
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="bookings" element={<MyBookings />} />
            <Route path="deliveries" element={<MyDeliveries />} />
            <Route path="notifications" element={<NotificationSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
